"use client";

/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { useAppDispatchV1 } from "@redux/hooks";

import { AsyncThunk, createAsyncThunk, nanoid } from "@reduxjs/toolkit";
import {
  APIResponse,
  ErrorCode,
  IfVoid,
  isError,
  LingoError,
  RequireOnlyOne,
} from "@thenounproject/lingo-core";
import type { AppDispatch, RootState } from "@redux/store";
import { useCallback, useState } from "react";
import { captureException } from "../../../adapters/sentry";
import { undoManager } from "@redux/undoManager/UndoManager";
import type { AsyncActionConfig } from "@redux/undoManager/types";
import { showNotification } from "@redux/actions/useNotifications";
import type { Message } from "@app/_components/Notifications";

type ThunkConfig = {
  state: RootState;
  rejectValue: LingoError;
  dispatch: AppDispatch;
};

type AsnycActionPayloadCreator<Result, Args> = (
  arg: Args,
  api: { getState: () => RootState; dispatch: AppDispatch }
) => Promise<Result>;

export type AsyncResponse<Result = unknown> = Promise<
  { isSuccess: boolean } & RequireOnlyOne<{ response?: Result; error?: LingoError }>
>;

export type HookAction<Result, Args> = (payload: Args) => AsyncResponse<Result>;

export type HookStatus<Result> = {
  isProcessing: boolean;
  error?: LingoError;
  result?: Result;
  reset: () => void;
};

export type AsyncHook<Result, Args> = () => readonly [HookAction<Result, Args>, HookStatus<Result>];

type ActionResult<Result, Extra> = IfVoid<Result, void, APIResponse<Result>> & Extra;
const asyncThunkCreator = createAsyncThunk.withTypes<ThunkConfig>();

type TypedAsyncAction<Result, Args> = AsyncThunk<Result, Args, ThunkConfig>;

// Overload: config-based signature
export default function createAsyncAction<Result = unknown, Args = void, Extra = void>(
  config: AsyncActionConfig<Args, ActionResult<Result, Extra>>,
  payloadCreator: AsnycActionPayloadCreator<ActionResult<Result, Extra>, Args>
): [
  AsyncHook<ActionResult<Result, Extra>, Args>,
  TypedAsyncAction<ActionResult<Result, Extra>, Args>,
];
// Overload: legacy string signature
export default function createAsyncAction<Result = unknown, Args = void, Extra = void>(
  typePrefix: string,
  payloadCreator: AsnycActionPayloadCreator<ActionResult<Result, Extra>, Args>
): [
  AsyncHook<ActionResult<Result, Extra>, Args>,
  TypedAsyncAction<ActionResult<Result, Extra>, Args>,
];
// Implementation
export default function createAsyncAction<Result = unknown, Args = void, Extra = void>(
  configOrPrefix: string | AsyncActionConfig<Args, ActionResult<Result, Extra>>,
  payloadCreator: AsnycActionPayloadCreator<ActionResult<Result, Extra>, Args>
): [
  AsyncHook<ActionResult<Result, Extra>, Args>,
  TypedAsyncAction<ActionResult<Result, Extra>, Args>,
] {
  const config = typeof configOrPrefix === "string" ? undefined : configOrPrefix;
  const typePrefix = typeof configOrPrefix === "string" ? configOrPrefix : configOrPrefix.action;
  const undoConfig = config?.undo;

  const action = asyncThunkCreator(typePrefix, async (args: Args, thunkApi) => {
    // Capture "before" state for undo
    let captured: unknown;
    const shouldRecordUndo =
      undoConfig &&
      !undoManager.isPerformingUndoRedo &&
      (!undoConfig.condition || undoConfig.condition(args, thunkApi));

    if (shouldRecordUndo && undoConfig.captureState) {
      captured = undoConfig.captureState(args, thunkApi);
    }

    try {
      const res = await payloadCreator(args, thunkApi);

      // Show notification whenever a message is configured (outside undo/redo)
      let notificationId: string | undefined, description: Message | undefined;
      if (undoConfig?.message && !undoManager.isPerformingUndoRedo) {
        const resolved =
          typeof undoConfig.message === "function"
            ? undoConfig.message(args, thunkApi)
            : undoConfig.message;
        if (resolved) {
          description = resolved;
          notificationId = nanoid();
          thunkApi.dispatch(
            showNotification({
              id: notificationId + "_undo",
              message: description,
              ...(shouldRecordUndo && {
                action: {
                  text: "Undo",
                  onClick: () => void undoManager.performUndo(thunkApi.dispatch, thunkApi.getState),
                },
              }),
            })
          );
        }
      }

      // Push to undo stack only when condition passes
      if (shouldRecordUndo) {
        if (undoConfig.onResult) {
          captured = undoConfig.onResult(captured, res, args);
        }
        undoManager.push({
          description: description!,
          actionType: typePrefix,
          timestamp: Date.now(),
          notificationId: notificationId!,
          undo: (dispatch, getState) =>
            undoConfig.performUndo(args, captured, { dispatch, getState }),
          redo: undoConfig.performRedo
            ? (dispatch, getState) =>
                undoConfig.performRedo!(args, captured, { dispatch, getState })
            : async dispatch => {
                const result = await dispatch(action(args));
                if (undoConfig.onResult && action.fulfilled.match(result)) {
                  captured = undoConfig.onResult(captured, result.payload, args);
                }
              },
        });
      }

      return thunkApi.fulfillWithValue(res);
    } catch (err) {
      if (isError(err)) {
        return thunkApi.rejectWithValue(err);
      } else if (err) {
        captureException(err);
        console.error("An unexpected error occurred:", err);
        throw err;
      }
    }
  });
  const useAction = () => {
    const [isProcessing, setProcessing] = useState(false);
    const [error, setError] = useState<LingoError>();
    const [result, setResult] = useState<ActionResult<Result, Extra>>();
    const dispatch = useAppDispatchV1();

    const reset = useCallback(() => {
      setError(undefined);
      setResult(undefined);
    }, []);
    const performAction = useCallback(
      async (args: Args) => {
        setProcessing(true);
        setError(undefined);
        const res = await dispatch(action(args));
        setProcessing(false);
        if (action.fulfilled.match(res)) {
          setResult(res.payload);
          return { isSuccess: true, response: res.payload };
        } else if (action.rejected.match(res)) {
          if (!res.payload && res.error) {
            const err = {
              code: ErrorCode.unknown,
              message:
                "Something went wrong. We've been notified and are working on it. Please try again.",
            };
            setError(err);
            return { isSuccess: false, error: err };
          } else {
            setError(res.payload);
            return { isSuccess: false, error: res.payload };
          }
        }
      },
      [dispatch]
    );
    return [performAction, { isProcessing, error, result, reset }] as const;
  };
  return [useAction, action];
}
